import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography, IconButton, Button } from '@mui/material';
import { Close, IosShare } from '@mui/icons-material';
import { whenCoachmarkIdle } from '../utils/coachmarkGate';
import { useDialog } from '../contexts/DialogProvider';
import { isProbablyInstalled } from '../utils/pwa';

function IOSInstallPrompt() {
  const { alert } = useDialog();
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // iOS Safari 감지
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // iOS이고, 설치된 적이 없을 때만 표시
    // (standalone 실행 이력이 있으면 isProbablyInstalled 가 true → Safari 탭 재접속에도 안 뜸)
    if (isIOS && !isProbablyInstalled()) {
      const hasSeenPrompt = localStorage.getItem('ios-install-prompt-seen');
      const lastShown = localStorage.getItem('ios-install-prompt-last-shown');
      const now = Date.now();
      
      // 한 번도 안 봤거나, 7일이 지났으면 다시 표시
      if (!hasSeenPrompt || (lastShown && now - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000)) {
        setTimeout(() => whenCoachmarkIdle(() => setShowPrompt(true)), 3000); // 3초 후 표시 (기능 안내 코치마크가 떠 있으면 닫힌 뒤)
      }
    }
  }, []);

  const handleClose = () => {
    setShowPrompt(false);
    localStorage.setItem('ios-install-prompt-seen', 'true');
    localStorage.setItem('ios-install-prompt-last-shown', Date.now().toString());
  };

  const handleInstall = () => {
    // iOS는 수동으로 설치해야 하므로 안내만 표시
    alert({ title: '앱 설치 방법', message: '하단 공유 버튼을 탭한 후 "홈 화면에 추가"를 선택하세요.' });
  };

  if (!showPrompt) return null;

  return (
    <Alert
      severity="info"
      sx={{
        position: 'fixed',
        bottom: {
          xs: 'calc(70px + env(safe-area-inset-bottom, 0px))',
          sm: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        },
        left: { xs: 16, sm: 20 },
        right: { xs: 16, sm: 'auto' },
        maxWidth: { sm: 400 },
        zIndex: 9999,
        boxShadow: 6,
        borderRadius: 2,
        animation: 'slideUp 0.3s ease-out'
      }}
      action={
        <IconButton size="small" onClick={handleClose} sx={{ color: 'inherit' }}>
          <Close />
        </IconButton>
      }
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        📱 앱처럼 사용하기
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              flexShrink: 0
            }}
          >
            1
          </Box>
          <Typography variant="caption">
            하단 <IosShare sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} /> 공유 버튼 탭
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              flexShrink: 0
            }}
          >
            2
          </Box>
          <Typography variant="caption">
            "홈 화면에 추가" 선택
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={handleClose}
          fullWidth
          sx={{ textTransform: 'none' }}
        >
          나중에
        </Button>
        <Button 
          size="small" 
          variant="contained" 
          onClick={handleInstall}
          fullWidth
          sx={{ textTransform: 'none' }}
        >
          설치 방법 보기
        </Button>
      </Box>
    </Alert>
  );
}

export default IOSInstallPrompt;